import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import { CashVelocityData } from '@/lib/types';

interface CashVelocityChartProps {
  data: CashVelocityData[];
}

export function CashVelocityChart({ data }: CashVelocityChartProps) {
  const chartData = useMemo(() => {
    return data.map(d => ({
      ...d,
      daysToCash: Number(d.daysToCash.toFixed(1)),
    }));
  }, [data]);

  return (
    <div className="glass-card-elevated p-4 h-[320px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm">Cash Velocity</h3>
          <p className="text-xs text-muted-foreground">Days to payment over time</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-primary rounded" />
            <span className="text-muted-foreground">Days to Cash</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary/20 rounded" />
            <span className="text-muted-foreground">Target Zone</span>
          </div>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height="85%">
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="targetGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(var(--border))" 
            vertical={false}
          />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis 
            domain={[0, 20]}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => `${value}d`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelStyle={{ color: 'hsl(var(--foreground))' }}
            formatter={(value: number, name: string) => [
              `${value} days`,
              name === 'daysToCash' ? 'Days to Cash' : name
            ]}
          />
          
          {/* Target zone area */}
          <Area
            dataKey="targetMax"
            fill="url(#targetGradient)"
            stroke="none"
          />
          <ReferenceLine y={7} stroke="hsl(var(--primary))" strokeDasharray="4 4" strokeOpacity={0.5} />
          <ReferenceLine y={10} stroke="hsl(var(--primary))" strokeDasharray="4 4" strokeOpacity={0.5} />
          
          <Line
            type="monotone"
            dataKey="daysToCash"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            dot={false}
            activeDot={{ 
              r: 4, 
              fill: 'hsl(var(--primary))',
              stroke: 'hsl(var(--background))',
              strokeWidth: 2 
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
